import {
  plaintextValidator,
  numberValidator,
  multiselectValidator
} from '../fields';
import { getTranslatedText } from '@nile/components';
import isEmptyValidator from '../utils/isEmpty';
import { isEmpty } from 'lodash';

const config = { mandatory: true };

const validateActionType = {
  actionType: plaintextValidator(config)
};

const validateCreateAlert = {
  actionType: plaintextValidator(config),
  alertSeverity: plaintextValidator(config),
  alertName: plaintextValidator(config)
};

const validateRunDefaultScript = {
  actionType: plaintextValidator(config),
  'script.scriptId': plaintextValidator(config)
};

const validateEmailNotification = {
  actionType: plaintextValidator(config),
  'notification.id': plaintextValidator(config)
};

const validateRunScript = {
  actionType: plaintextValidator(config),
  'script.scriptId': plaintextValidator(config),
  scriptArguments: {
    array: {
      name: plaintextValidator(config),
      value: plaintextValidator(config)
    }
  }
};

const validateCreateTicket = {
  actionType: plaintextValidator(config),
  'ticketData.dataId': numberValidator({
    mandatory: true,
    optional: { greaterThan: 0 }
  }),
  'resetActions.actionType': plaintextValidator(config)
};

const validateResetTicketStatus = {
  resetTicketStatus: plaintextValidator(config)
};

const policyNameValidator = {
  name: plaintextValidator(config)
};

const basicAlertValidation = (intl) => {
  return {
    ...policyNameValidator,
    coolingOffPeriod: numberValidator({
      mandatory: true,
      optional: {
        greaterThanOrEqualTo: 30,
        lessThanOrEqualTo: 4320,
        notGreaterThanOrEqualTo: `^${getTranslatedText(
          intl,
          'admin.policyManagement.alert.cooloff.hour.limit.error'
        )}`,
        notLessThanOrEqualTo: `^${getTranslatedText(
          intl,
          'admin.policyManagement.alert.cooloff.hour.limit.error'
        )}`
      }
    })
  };
};

const durationValidation = (data) => {
  const { optional } = data || {};
  return {
    'duration.unit': plaintextValidator({ ...config }),
    'duration.value': numberValidator({
      mandatory: true,
      optional: optional || { greaterThan: 0 }
    })
  };
};

const validateDiskFreeSpace = {
  'condition.attribute': plaintextValidator(config),
  'condition.operator': plaintextValidator(config),
  'condition.value.thresholdUnit': plaintextValidator(config),
  'condition.value.thresholdValue': numberValidator({
    mandatory: true,
    optional: { greaterThan: 0 }
  }),
  ...durationValidation()
};

const validateDiskActiveTime = {
  'condition.attribute': plaintextValidator(config),
  'condition.operator': plaintextValidator(config),
  'condition.value.thresholdValue': numberValidator({
    mandatory: true,
    optional: { greaterThan: 0 }
  }),
  ...durationValidation()
};

const networkMonitorBaseValidation = {
  'condition.attribute': plaintextValidator(config),
  'condition.operator': plaintextValidator(config),
  'condition.value.value': numberValidator(config),
  ...durationValidation()
};

const validateCPUUtilization = {
  'condition.attribute': plaintextValidator(config),
  'condition.operator': plaintextValidator(config),
  'condition.value.thresholdValue': numberValidator({
    mandatory: true,
    optional: { greaterThan: 0 }
  }),
  ...durationValidation()
};

const validateMemoryUtilization = {
  'condition.attribute': plaintextValidator(config),
  'condition.operator': plaintextValidator(config),
  'condition.value.thresholdValue': numberValidator({
    mandatory: true,
    optional: { greaterThan: 0 }
  }),
  ...durationValidation()
};

const validateNetUtilization = {
  'condition.attribute': plaintextValidator(config),
  'condition.operator': plaintextValidator(config),
  'condition.value.thresholdValue': numberValidator({
    mandatory: true,
    optional: { greaterThan: 0 }
  }),
  ...durationValidation()
};

const validateNetInterfaceStatus = {
  'condition.attribute': plaintextValidator(config),
  'condition.value.operationStatus': plaintextValidator(config),
  ...durationValidation()
};

const validateCustomField = (type, operator) => {
  let valueValidator;
  switch (type) {
    case 'number':
      valueValidator = numberValidator(config);
      break;
    case 'multiselect':
      valueValidator = multiselectValidator(config);
      break;
    default:
      valueValidator = plaintextValidator(config);
      break;
  }

  let conditions = {
    'condition.attribute': plaintextValidator(config),
    'condition.operands': {
      array: {
        fields: plaintextValidator(config),
        operator: plaintextValidator(config),
        value:
          operator !== 'isEmpty' && operator !== 'isNotEmpty'
            ? valueValidator
            : null
      }
    }
  };

  return conditions;
};

const durationMinMaxMap = {
  minutes: {
    min: 5
  },
  hours: {
    min: 1
  }
};

const validateAgentStatus = (unit, intl) => {
  return {
    'condition.attribute': plaintextValidator(config),
    ...durationValidation({
      optional: {
        greaterThanOrEqualTo: durationMinMaxMap[unit].min,
        notGreaterThanOrEqualTo: getTranslatedText(
          intl,
          'admin.policyManagement.alert.agentStatus.durationError'
        )
      }
    })
  };
};

const validateCreateAlertForIA = {
  actionType: plaintextValidator(config),
  alertName: plaintextValidator(config)
};

const validateCoolingOffPeriodForIA = (intl) => {
  return {
    coolingOffPeriod: numberValidator({
      mandatory: true,
      optional: {
        greaterThanOrEqualTo: 30,
        lessThanOrEqualTo: 4320,
        notGreaterThanOrEqualTo: `^${getTranslatedText(
          intl,
          'admin.policyManagement.alert.cooloff.hour.limit.error'
        )}`,
        notLessThanOrEqualTo: `^${getTranslatedText(
          intl,
          'admin.policyManagement.alert.cooloff.hour.limit.error'
        )}`
      }
    })
  };
};

const validateProcessStatus = {
  'condition.attribute': plaintextValidator(config),
  'condition.value.status': plaintextValidator(config),
  'condition.value.processName': plaintextValidator(config),
  ...durationValidation()
};

const validateServiceStatus = {
  'condition.attribute': plaintextValidator(config),
  'condition.value.status': plaintextValidator(config),
  'condition.value.serviceName': plaintextValidator(config),
  ...durationValidation()
};

const validateEventLog = (criteria, intl) => {
  const { eventId, eventSource, eventType, searchOperator, searchValue } =
    criteria?.condition?.value || {};

  const errorMap = {};

  if (
    isEmpty(eventId) &&
    isEmpty(eventSource) &&
    isEmpty(eventType) &&
    isEmpty(searchOperator && searchValue)
  ) {
    errorMap.eventLogNoOption = getTranslatedText(
      intl,
      'admin.policyManagement.alert.eventLog.optionError'
    );
  }

  if (
    (!isEmpty(searchOperator) && isEmpty(searchValue)) ||
    (isEmpty(searchOperator) && !isEmpty(searchValue))
  ) {
    errorMap.eventLogOperatorError = getTranslatedText(
      intl,
      'admin.policyManagement.alert.eventLog.operatorError'
    );
  }

  return errorMap;
};

export {
  policyNameValidator,
  basicAlertValidation,
  validateCreateAlert,
  validateRunDefaultScript,
  validateEmailNotification,
  validateRunScript,
  validateActionType,
  validateDiskFreeSpace,
  validateDiskActiveTime,
  validateCPUUtilization,
  validateMemoryUtilization,
  validateNetUtilization,
  validateNetInterfaceStatus,
  validateCreateTicket,
  validateAgentStatus,
  validateResetTicketStatus,
  validateCreateAlertForIA,
  validateCoolingOffPeriodForIA,
  validateProcessStatus,
  validateServiceStatus,
  validateEventLog,
  validateCustomField,
  networkMonitorBaseValidation
};
